/**
 * Hook para gerenciar lista de inboxes com Supabase Realtime
 * 
 * ARQUITETURA:
 * - Usa RPC get_tenant_inboxes_safe para não expor provider_id, slug, instance UUIDs
 * - Usa React Query para cache e estado
 * - Usa Supabase Realtime para atualizações instantâneas
 * - Escuta eventos INSERT, UPDATE, DELETE na tabela inboxes
 * - Invalida cache automaticamente quando dados mudam
 * - Sem polling - apenas 1 query inicial + eventos em tempo real
 * 
 * @version 1.1.0
 * @date 2026-03-29
 * @param tenantId - ID do tenant para filtrar as inboxes
 * @returns Query com lista de inboxes e função refetch
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import type { ChannelType, InboxStatus } from '@/components/tenant/InboxCard';

// Logger para este hook
const logger = createLogger('useInboxes');

// Interface para instância vinculada (v1.1.0: sem UUID exposto)
export interface LinkedInstance {
  id: string;
  instance_name: string;
  status: string;
  provider_display_name?: string;
}

// Interface para inbox com dados do provider e instância
export interface InboxData {
  id: string;
  name: string;
  channel_type: ChannelType;
  identifier: string;
  status: InboxStatus;
  is_default?: boolean;
  provider?: {
    name: string;
    display_name?: string;
    logo_url?: string;
    slug?: string;
  };
  provider_version?: {
    version: string;
    status: string;
  };
  /** Instância vinculada à inbox */
  linked_instance?: LinkedInstance | null;
}

/**
 * Busca inboxes do tenant atual via RPC server-side segura
 * Atualizações são recebidas instantaneamente via WebSocket
 * 
 * @param tenantId - ID do tenant para filtrar as inboxes
 * @returns Query com lista de inboxes
 */
export function useInboxes(tenantId: string | undefined) {
  // Realtime removido deste hook - centralizado em useInboxPageRealtime.ts
  // O React Query compartilha cache pela queryKey, então invalidateQueries
  // disparado pelo hook centralizado atualiza todas as instâncias deste hook.

  // Query via RPC server-side (sem expor dados sigilosos no network)
  const query = useQuery({
    queryKey: ['inboxes', tenantId],
    queryFn: async (): Promise<InboxData[]> => {
      if (!tenantId) return [];

      logger.debug('Buscando inboxes via RPC segura', { tenantId });

      const { data, error } = await supabase.rpc('get_tenant_inboxes_safe', {
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao buscar inboxes', { error });
        throw error;
      }

      // Mapeia retorno da RPC para o formato esperado pelo frontend
      const formattedInboxes: InboxData[] = (data || []).map((inbox: any) => ({
        id: inbox.id,
        name: inbox.name,
        channel_type: inbox.channel_type as ChannelType,
        identifier: inbox.identifier,
        status: inbox.status as InboxStatus,
        is_default: inbox.is_default || false,
        provider: inbox.provider_display_name ? {
          // v1.2.0: RPC agora retorna provider_name (canônico) e provider_slug.
          // Mantemos display_name para a UI, mas o slug é a fonte da verdade
          // para dispatch (ex: "Configurar Webhooks" → edge function correta).
          name: inbox.provider_name || inbox.provider_display_name,
          display_name: inbox.provider_display_name || undefined,
          logo_url: inbox.provider_logo_url || undefined,
          slug: inbox.provider_slug || undefined,
        } : undefined,
        provider_version: inbox.provider_version_label ? {
          version: inbox.provider_version_label,
          status: inbox.provider_version_status || 'unknown',
        } : undefined,
        linked_instance: inbox.instance_display_name && inbox.instance_status ? {
          id: '', // UUID da instância não é mais exposto (segurança)
          instance_name: inbox.instance_display_name,
          status: inbox.instance_status,
          provider_display_name: inbox.provider_display_name || undefined,
        } : null,
      }));

      logger.debug('Inboxes carregadas', { count: formattedInboxes.length });
      return formattedInboxes;
    },
    enabled: !!tenantId,
    // Sem polling - Realtime centralizado em useInboxPageRealtime.ts
  });

  return query;
}
